import type { Field } from '../field';
import { DataOption } from '../../forms/types/FormDefinition';

/**
 * Function to return a single field instances by ID.
 */
export function getFieldById({ id, fields }: { id: string, fields: Field[] }): Field | undefined {
  if (!fields?.length || !id) return undefined;
  return fields.find((field) => field.getId() === id);
}

/**
 * Function to return multiple field instances by ID.
 *
 * Firstly we find these fields, and then order them by the order of the `ids` parameter.
 */
export function getFieldsById({ ids, fields }: { ids: string[], fields: Field[] }): Field[] {
  if (!fields?.length || !ids?.length) return [];

  const fieldInstances = fields.filter((field) => ids.includes(field.getId()));

  return ids.map((id) => fieldInstances
    .find((instance) => instance.id === id))
    .filter((instance) => instance !== null && instance !== undefined) as Field[];
}

/**
 * Get a fields border colour based on its validation.
 */
export function getErrorClass(field: Field): string {
  const fieldErrors = field.getErrors();

  if (!fieldErrors?.length) return '';

  let fieldColour = '';

  fieldErrors.forEach((error) => {
    if (error.level === 'warning' && fieldColour !== 'error') fieldColour = 'warning';
    if (error.level === 'critical') fieldColour = 'critical';
  });

  return fieldColour;
}


/**
 * Return data options related to a given type.
 */
export function getDataOptions(
  { type, options } : { type: string, options: DataOption[] },
): DataOption[] {
  return options.filter((option) => option.type === type);
}

/**
 * Get all of the children for a given field recursively
 * This will get grandchildren the grandestchildren etc
 */
export function getAllChildren(field: Field, fields: Field[]): Field[] {
  function getChildren(parent: Field, first?: boolean): Field[] {
    if (parent && parent.fieldDef.children && parent.fieldDef.children.length) {
      let children = first ? [] : [parent];
      parent.fieldDef.children!.forEach((childId: string) => {
        const child = fields.find((f) => f.id === childId);
        children = [...children, ...getChildren(child!)];
      });
      return children;
    } if (!first) {
      return [parent];
    }

    return [];
  }

  const result = getChildren(field, true);
  return result.filter((f) => !!f);
}
